"use client";

import React, { Ref, useImperativeHandle, useState } from "react";
import {
  createContext,
  HTMLAttributes,
  PropsWithChildren,
  useContext,
} from "react";
import Typography from "./typography";
import { Heading } from "./typography/Heading";

interface ITabViewContextProps {
  activeTabKey: string;
  setActiveTab?: (tabKey: string) => void;
}

const TabViewContext = createContext<ITabViewContextProps>({
  activeTabKey: "",
});

export type TabRef = {
  changeTab: (tabKey: string) => void;
};

type Tabs = Array<{ key: string; label: string }>;
type TabsProps = {
  tabs: Tabs;
  children?: React.ReactElement<TabItemProps>[]; // 👈 only TabItem children
  onTabChange?: (tabKey: string) => void;
  variant:
    | "flat"
    | "primary-rounded"
    | "primary-rounded-blue"
    | "flat-rounded"
    | "primary-rounded-no-shadows"
    | "primary-rounded-no-shadows-bgColor"
    | "vertical-flat-rounded"
    | "underline-flat-rounded"
    | "black-rounded"
    | "black-rounded-no-grid";
  applyContentContainer?: boolean;
  startingIndex?: number;
  horizontal?: boolean;
  as?: "h3";

  ref?: Ref<TabRef>;
};
export function Tabs({
  ref,
  ...props
}: TabsProps & HTMLAttributes<HTMLDivElement>) {
  const [activeTabKey, setActiveTabKey] = useState(
    props.tabs.at(props.startingIndex ?? 0)?.key || "",
  );

  useImperativeHandle(ref, () => {
    return {
      changeTab(tabKey) {
        setActiveTabKey(tabKey);
      },
    };
  });

  const changeTab = (tabKey: string) => {
    setActiveTabKey(tabKey);
    if (props.onTabChange) {
      props.onTabChange(tabKey);
    }
  };

  // THIS IS ONLY CONTENT THAT IS NEXT TO THE TAB NOT UNDERNEATH IT!
  if (props.horizontal) {
    return (
      <TabViewContext.Provider
        value={{ activeTabKey, setActiveTab: changeTab }}
      >
        <aside className="flex gap-128px">
          <div
            className={`${
              props.applyContentContainer && "contentContainer z-30"
            }`}
          >
            {props.variant == "flat" && (
              <FlatTabs
                activeTab={activeTabKey}
                changeTab={changeTab}
                isRounded={false}
                {...props}
              />
            )}
            {props.variant == "flat-rounded" && (
              <FlatTabs
                activeTab={activeTabKey}
                changeTab={changeTab}
                isRounded={true}
                {...props}
              />
            )}
            {props.variant == "primary-rounded" && (
              <RoundedTabs
                activeTab={activeTabKey}
                changeTab={changeTab}
                {...props}
              />
            )}
            {props.variant == "primary-rounded-blue" && (
              <RoundedTabs
                activeTab={activeTabKey}
                changeTab={changeTab}
                blue
                {...props}
              />
            )}

            {props.variant == "primary-rounded-no-shadows" && (
              <RoundedTabs
                activeTab={activeTabKey}
                changeTab={changeTab}
                enableShadows={false}
                {...props}
              />
            )}

            {props.variant == "vertical-flat-rounded" && (
              <VerticalFlatTabs
                activeTab={activeTabKey}
                changeTab={changeTab}
                {...props}
              />
            )}
          </div>
          {props.children}
        </aside>
      </TabViewContext.Provider>
    );
  }

  return (
    <TabViewContext.Provider value={{ activeTabKey, setActiveTab: changeTab }}>
      <div
        className={`${props.applyContentContainer && "contentContainer z-30"}`}
      >
        {props.variant == "flat" && (
          <FlatTabs
            as={props.as}
            activeTab={activeTabKey}
            changeTab={changeTab}
            isRounded={false}
            {...props}
          />
        )}
        {props.variant == "flat-rounded" && (
          <FlatTabs
            activeTab={activeTabKey}
            changeTab={changeTab}
            isRounded={true}
            {...props}
          />
        )}
        {props.variant == "primary-rounded" && (
          <RoundedTabs
            activeTab={activeTabKey}
            changeTab={changeTab}
            {...props}
          />
        )}

        {props.variant == "primary-rounded-blue" && (
          <RoundedTabs
            activeTab={activeTabKey}
            changeTab={changeTab}
            blue
            {...props}
          />
        )}

        {props.variant == "primary-rounded-no-shadows" && (
          <RoundedTabs
            activeTab={activeTabKey}
            changeTab={changeTab}
            enableShadows={false}
            {...props}
          />
        )}

        {props.variant == "primary-rounded-no-shadows-bgColor" && (
          <RoundedTabs
            activeTab={activeTabKey}
            changeTab={changeTab}
            enableShadows={false}
            {...props}
          />
        )}

        {props.variant == "vertical-flat-rounded" && (
          <VerticalFlatTabs
            activeTab={activeTabKey}
            changeTab={changeTab}
            {...props}
          />
        )}

        {props.variant == "underline-flat-rounded" && (
          <UnderlineFlatTabs
            as={props.as}
            activeTab={activeTabKey}
            changeTab={changeTab}
            {...props}
          />
        )}

        {props.variant == "black-rounded" && (
          <BlackRoundedTabs
            as={props.as}
            activeTab={activeTabKey}
            changeTab={changeTab}
            {...props}
          />
        )}
        {props.variant == "black-rounded-no-grid" && (
          <BlackRoundedTabsNoGrid
            as={props.as}
            activeTab={activeTabKey}
            changeTab={changeTab}
            {...props}
          />
        )}
      </div>
      {props.children}
    </TabViewContext.Provider>
  );
}

type TabItemProps = {
  tabKey: string;
};
export function TabItem(
  props: PropsWithChildren<TabItemProps> & HTMLAttributes<HTMLDivElement>,
) {
  const tabContext = useContext(TabViewContext);
  return (
    <section
      className={`${
        props.tabKey == tabContext.activeTabKey ? "block" : "hidden"
      } ${props.className}`}
    >
      {props.children}
    </section>
  );
}

// Tab Variants

type TabVariantProps = {
  tabs: Tabs;
  as?: "h3";
  changeTab: (tabKey: string) => void;
  activeTab: string;
};

type FlatTabsOptionalProps = {
  isRounded?: boolean;
  as?: "h3";
};

type VerticalFlatTabsProps = TabVariantProps & HTMLAttributes<HTMLDivElement>;

export function VerticalFlatTabs(props: VerticalFlatTabsProps) {
  return (
    <div className="flex flex-col gap-2.5 bg-bgColor lg:w-[265px] w-fit rounded-14 items-center p-5">
      {props.tabs.map((data, key) => (
        <button
          onClick={() => props.changeTab(data.key)}
          key={`tab-${key}`}
          className={`
              ${"px-2 py-2 sm:px-5 "}
              text-center cursor-pointer  text-nowrap
              lg:w-fit w-full
               ${props.activeTab == data.key ? "bg-white shadow-vertical-flat-tabs" : "bg-none"} 
               rounded-md
              `}
        >
          <Heading size="h6" as="p">
            {data.label}
          </Heading>
        </button>
      ))}
    </div>
  );
}

type FlatTabsProps = TabVariantProps &
  HTMLAttributes<HTMLDivElement> &
  FlatTabsOptionalProps;

export function FlatTabs(props: FlatTabsProps) {
  return (
    <div
      className={`
      bg-bgColor p-2.5
      ${props.isRounded ? `rounded-full` : `rounded-lg`} 
      flex flex-row w-fit
      ${props.className}`}
    >
      {props.tabs.map((data, key) => (
        <button
          onClick={() => props.changeTab(data.key)}
          key={`tab-${key}`}
          className={`
              ${props.isRounded ? "px-8 py-2.5" : "py-2 px-2 sm:px-5 "}
              text-center cursor-pointer 
              lg:w-fit w-full
               ${
                 props.activeTab == data.key ? "bg-white shadow-md" : "bg-none"
               } 
               ${props.isRounded ? `rounded-full` : "rounded-md"}
              `}
        >
          <Typography
            as={props.as || "p"}
            size="body1"
            color={props.activeTab == data.key ? "black" : "darkGray"}
          >
            {data.label}
          </Typography>
        </button>
      ))}
    </div>
  );
}

type UnderlineFlatTabsProps = TabVariantProps & HTMLAttributes<HTMLDivElement>;

export function UnderlineFlatTabs(props: UnderlineFlatTabsProps) {
  return (
    <div className="grid grid-rows-1 grid-flow-col bg-none rounded-md items-center">
      {props.tabs.map((data, key) => (
        <button
          onClick={() => props.changeTab(data.key)}
          key={`tab-${key}`}
          className={`
              text-center cursor-pointer
               w-full
               ${props.activeTab == data.key ? "bg-none" : "t"}  
               ${"rounded-md"}
              `}
        >
          <Heading size="h6" as={props.as || "p"} className="lg:px-5 mb-5px">
            {data.label}
          </Heading>
          <div
            className={`w-full h-0.5 ${props.activeTab == data.key ? "bg-black" : "bg-bgColor"}`}
          ></div>
        </button>
      ))}
    </div>
  );
}

type RoundedTabsOptionalProps = {
  enableShadows?: boolean;
  blue?: boolean;
};
export function RoundedTabs({
  enableShadows = true,
  blue = false,
  ...props
}: TabVariantProps &
  RoundedTabsOptionalProps &
  HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`${props.className} bg-white rounded-full p-2.5 gap-2.5 flex w-fit `}
    >
      {props.tabs.map((data, key) => (
        <button
          onClick={() => props.changeTab(data.key)}
          key={`tab-${key}`}
          className={`cursor-pointer px-5 lg:px-8 py-2 text-sm lg:w-fit w-full rounded-full 
              ${
                props.activeTab === data.key
                  ? `text-white ${blue ? " bg-[#186CFF]" : "bg-main"} ${
                      !enableShadows ? "shadow-none" : "shadow-md"
                    }`
                  : "text-dark-gray hover:text-gray-800"
              }`}
        >
          <Typography as="h3" size="unresponsiveBody1">
            {data.label}
          </Typography>
        </button>
      ))}
    </div>
  );
}

type BlackRoundedTabs = TabVariantProps & HTMLAttributes<HTMLDivElement>;

export function BlackRoundedTabs(props: BlackRoundedTabs) {
  return (
    <div
      className={`bg-none rounded-full  gap-2.5 grid grid-cols-2 lg:flex ${props.className}`}
    >
      {props.tabs.map((data, key) => (
        <button
          onClick={() => props.changeTab(data.key)}
          key={`tab-${key}`}
          className={`cursor-pointer lg:px-8 px-5 py-1.5 lg:py-2 text-sm lg:w-fit w-full rounded-full 
              ${
                props.activeTab === data.key
                  ? `text-white bg-black`
                  : "text-black bg-bgColor hover:text-gray-800"
              }`}
        >
          <Typography as={props.as || "p"} size="body1">
            {data.label}
          </Typography>
        </button>
      ))}
    </div>
  );
}

export function BlackRoundedTabsNoGrid(props: BlackRoundedTabs) {
  return (
    <div
      className={`bg-none rounded-full gap-2.5 flex flex-wrap ${props.className}`}
    >
      {props.tabs.map((data, key) => (
        <button
          onClick={() => props.changeTab(data.key)}
          key={`tab-${key}`}
          className={`cursor-pointer lg:px-8 px-5 py-1.5 lg:py-2 text-sm w-fit rounded-full 
              ${
                props.activeTab === data.key
                  ? `text-white bg-black`
                  : "text-black bg-bgColor hover:text-gray-800"
              }`}
        >
          <Typography as={props.as || "p"} size="body1">
            {data.label}
          </Typography>
        </button>
      ))}
    </div>
  );
}
